import { ArrowRight, CalendarRange, ChevronLeft, ChevronRight, Image } from "lucide-react";
import React, { useState, useEffect, useRef, useCallback } from "react";

const API_URL = "https://portal.lsb.go.ke/lsb/news/";
const NEWS_IMAGE_BASE_URL = "https://portal.lsb.go.ke";

const NewsCarousel = () => {
  const [newsData, setNewsData] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPaused, setIsPaused] = useState(false);

  const intervalRef = useRef(null);
  const intervalDuration = 8000;

  // Fetch news with AbortController for cleanup
  useEffect(() => {
    const abortController = new AbortController();

    const fetchNews = async () => {
      try {
        setLoading(true);
        const response = await fetch(API_URL, { signal: abortController.signal });

        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const data = await response.json();
        setNewsData(Array.isArray(data) ? data : []);
        setError(null);
      } catch (err) {
        if (err.name !== 'AbortError') {
          setError("Unable to load news. Please try again later.");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchNews();

    return () => abortController.abort();
  }, []);

  // Reset and start autoplay
  const startAutoplay = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);

    intervalRef.current = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % newsData.length);
    }, intervalDuration);
  }, [newsData.length]);

  // Autoplay management
  useEffect(() => {
    if (newsData.length === 0 || isPaused) return;

    startAutoplay();
    return () => clearInterval(intervalRef.current);
  }, [newsData.length, isPaused, startAutoplay]);

  // Navigate to specific slide
  const goToSlide = useCallback((index) => {
    setActiveIndex(index);
  }, []);

  const nextSlide = useCallback(() => {
    setActiveIndex((prev) => (prev + 1) % newsData.length);
  }, [newsData.length]);

  const prevSlide = useCallback(() => {
    setActiveIndex((prev) => (prev === 0 ? newsData.length - 1 : prev - 1));
  }, [newsData.length]);

  // Touch handlers
  const touchStart = useRef({ x: 0 });
  const handleTouchStart = (e) => {
    touchStart.current.x = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    const deltaX = e.changedTouches[0].clientX - touchStart.current.x;
    if (Math.abs(deltaX) > 50) {
      deltaX > 0 ? prevSlide() : nextSlide();
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        prevSlide();
        e.preventDefault();
      } else if (e.key === 'ArrowRight') {
        nextSlide();
        e.preventDefault();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [prevSlide, nextSlide]);

  // Loading State
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-96 bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl">
        <div className="relative">
          <div className="w-16 h-16 border-4 border-[#ebcab3] border-t-[#3d0707] rounded-full animate-spin"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-8 h-8 bg-[#3d0707] rounded-full animate-pulse"></div>
          </div>
        </div>
        <p className="mt-6 text-lg font-medium text-[#3d0707]">Loading latest news...</p>
      </div>
    );
  }

  // Error State
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-96 bg-red-50 rounded-2xl border-2 border-red-200 p-8">
        <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mb-4">
          <svg className="w-10 h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <p className="text-red-600 text-center font-medium">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="mt-4 px-6 py-2 bg-[#3d0707] text-white rounded-lg hover:bg-[#5a0a0a] transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  // Empty State
  if (newsData.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-96 bg-gray-50 rounded-2xl">
        <svg className="w-24 h-24 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
        </svg>
        <p className="text-gray-500 text-lg">No news available at the moment</p>
        <p className="text-gray-400 text-sm mt-1">Check back later for updates</p>
      </div>
    );
  }

  const currentNews = newsData[activeIndex];
  const imageUrl = currentNews?.document
    ? currentNews.document.startsWith("http")
      ? currentNews.document
      : `${NEWS_IMAGE_BASE_URL}${currentNews.document}`
    : null;

  return (
    <div
      className="relative"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {/* Main Carousel Container */}
      <div className="relative overflow-hidden rounded-2xl shadow-2xl bg-black">
        {/* Image Section - Full height */}
        <div
          className="relative h-[500px] md:h-[600px]"
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={currentNews?.title || "News image"}
              className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900">
              <Image className="w-24 h-24 text-gray-600" />
            </div>
          )}

          {/* Overlay Container - Bottom section with gradient */}
          <div className="absolute inset-x-0 bottom-0">
            {/* Main content overlay - solid at bottom */}
            <div className="bg-gradient-to-t from-black via-black/95 to-black/20 backdrop-blur-sm">
              <div className="px-6 md:px-8 py-6 md:py-8">
                {/* Title */}
                <div className="font-medium text-white line-clamp-2" style={{ fontSize: "calc(0.6em + 0.6vw)" }}>
                  {currentNews?.title || "News Update"}
                </div>

                {/* Message/Description */}
                <div className="text-gray-200 leading-relaxed line-clamp-3 mb-4" style={{ fontSize: "calc(0.4em + 0.4vw)" }}>
                  {currentNews?.message || (
                    <span className="text-gray-400 italic">No additional details available.</span>
                  )}
                </div>

                {/* Read More Button */}
                {currentNews?.message && currentNews.message.length > 150 && (
                  <button className="text-[#ebcab3] text-white font-medium transition-colors inline-flex items-center gap-2 group">
                    <span>Read more</span> <ArrowRight className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" />
                  </button>
                )}

                {/* Metadata / Date (if available) */}
                {currentNews?.date && (
                  <div className="mt-3 flex items-center gap-2 text-gray-400 text-sm">
                    <CalendarRange className="w-4 h-4 text-white" />
                    <span>{new Date(currentNews.date).toLocaleDateString()}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Slide Indicator */}
          <div className="absolute top-4 right-4 bg-[#ebcab3] backdrop-blur-sm text-black px-2.5 py-3 rounded-full text-sm font-bold">
            {activeIndex + 1} / {newsData.length}
          </div>
        </div>
      </div>

      {/* Navigation Arrows */}
      <button
        onClick={prevSlide}
        className="absolute left-4 top-1/2 -translate-y-1/2 bg-[#ebcab3] hover:bg-[#3d0707] text-[#3d0707] hover:text-white rounded-full p-3 shadow-lg transition-all duration-300 hover:scale-110 backdrop-blur-sm z-10"
        aria-label="Previous slide"
      >
        <ChevronLeft className="w-5 h-5 md:w-6 md:h-6" />
      </button>

      <button
        onClick={nextSlide}
        className="absolute right-4 top-1/2 -translate-y-1/2 bg-[#ebcab3] hover:bg-[#3d0707] text-[#3d0707] hover:text-white rounded-full p-3 shadow-lg transition-all duration-300 hover:scale-110 backdrop-blur-sm z-10"
        aria-label="Next slide"
      >
        <ChevronRight className="w-5 h-5 md:w-6 md:h-6" />
      </button>

      {/* Dots Navigation */}
      <div className="flex justify-center gap-2 mt-6">
        {newsData.map((_, idx) => (
          <button
            key={idx}
            onClick={() => goToSlide(idx)}
            className={`transition-all duration-300 rounded-full ${idx === activeIndex
              ? "bg-[#3d0707] w-8 h-2.5"
              : "bg-[#ebcab3] hover:bg-[#3d0707]/50 w-2 h-2"
              }`}
            aria-label={`Go to slide ${idx + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

export default NewsCarousel;